import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { resolveTabVisible } from '../utils/tabAccess';
import { toast } from 'sonner';
import { Save, Plus, FileText, Search, Send, Trash2, X } from 'lucide-react';
import { PageHeader, Segmented } from '../components/ds';
import { CroquisEditor, SelectionBar } from '../components/croquis/CroquisEditor';
import { CroquisAdminSuivi } from '../components/croquis/CroquisAdminSuivi';
import html2canvas from 'html2canvas';

const CONCEPTS = ['Wasabi', 'Mme Wang', 'Sorae'];
const ENSEIGNES = ['Leclerc', 'Intermarché', 'Système U', 'Carrefour', 'Auchan'];
const TYPES = ['Nouveau contrat', 'Remodeling', 'Relifting'];
const IMPLANTATIONS = ['4 faces', '3 faces', '2 faces', 'Milieu des trad.', 'En bergerie'];
const FORMATS = ['Avec aérien - Herse', 'Avec canopée angle', 'Sans aérien - Herse', 'Strictement rien'];
const PRIORITES = ['Standard', 'Urgent'];
const HAB_GROUPS = {
  Enseignes: ['Wasabi', 'Mme Wang', 'Sorae'],
  'Façades': ['Noire lisse', 'Noire avec carreaux orange', 'Polaroïd Wasabi', 'Polaroïd Mme Wang', 'Polaroïd Sorae', 'Habillage bois vitrine', 'Habillage vitrine avec stickers', 'Habillage vitrine avec enseigne Wasabi', 'Habillage vitrine avec enseigne Mme Wang'],
  Communication: ['Carte affiche menu', 'Poisson', 'Lanterne', 'Stickers vitrage'],
};
const EQ_STATUTS = ['Neuf', 'Existant client à réemployer', 'Occasion DSAO', 'À confirmer'];

function computeEquip(d, catalogue) {
  const cm = {}; (catalogue || []).forEach((c) => { cm[c.k] = c; });
  const over = d.eq || {};
  const groups = {};
  (d.items || []).forEach((it) => {
    const c = cm[it.k];
    if (!c || ['point', 'fleche', 'repere'].includes(c.type) || c.free) return;
    const length = (c.sur_mesure && it.len) ? it.len : c.L;
    const key = c.sur_mesure ? `${c.k}#${Math.round(length)}` : c.k;
    if (!groups[key]) {
      const o = over[key] || {};
      groups[key] = { key, libelle: c.libelle, qte: 0, L: Math.round(length), P: c.P, statut: o.statut || 'Neuf', precision: o.precision || '' };
    }
    groups[key].qte += 1;
  });
  return Object.values(groups);
}

async function compressImage(file) {
  if (!file.type.startsWith('image/')) return file;
  try {
    const img = document.createElement('img');
    const url = URL.createObjectURL(file);
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const max = 1600;
    let { width, height } = img;
    if (width > max || height > max) { const r = Math.min(max / width, max / height); width = Math.round(width * r); height = Math.round(height * r); }
    const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.8));
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch { return file; }
}

function frDate(d) {
  if (!d) return '';
  try { const x = new Date(d); return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`; } catch { return d; }
}

// §8.4 phrase d'intention
function intentText(d, catalogue) {
  if (!d) return '';
  const cat = (k) => catalogue.find((c) => c.k === k) || {};
  const type = d.typeContrat || 'Nouveau contrat';
  let impl = 'corner';
  if (['4 faces', '3 faces', '2 faces'].includes(d.format)) impl = `corner ${d.format}`;
  else if (d.format === 'Milieu des trad.') impl = 'corner implanté au milieu des rayons trad.';
  else if (d.format === 'En bergerie') impl = 'corner implanté en bergerie';
  const fmt = d.formatProjet ? ` ${d.formatProjet.toLowerCase()}` : '';
  const items = d.items || [];
  const vitrines = items.filter((i) => cat(i.k).libelle?.startsWith('Vitrine')).map((i) => (i.w || cat(i.k).L));
  const seg = [];
  seg.push(`${type} — ${impl}${fmt} de ${d.L} × ${d.P} cm (largeur × profondeur)`);
  if (vitrines.length) seg.push(`${vitrines.length} vitrine(s) (${vitrines.join(' + ')})`);
  const sal = items.find((i) => cat(i.k).libelle?.startsWith('Saladette'));
  if (sal) seg.push(`saladette ${cat(sal.k).L}`);
  const plg = items.find((i) => cat(i.k).libelle?.startsWith('Plonge'));
  if (plg) seg.push(`plonge ${cat(plg.k).L}`);
  if (items.some((i) => i.k === 'wok' || i.k === 'wokc')) seg.push('poste wok');
  let phrase = seg.join(', ');
  if (d.retour) phrase += `. Retour visuel souhaité le ${frDate(d.retour)}`;
  if (d.priorite === 'Urgent') phrase += ' (urgent)';
  return phrase + '.';
}

function projectTitle(d) {
  const concepts = (d.concept || []).join(' + ');
  return `${concepts} ${d.enseigne || ''} ${d.ville || ''}`.toUpperCase().trim();
}

function projetName(d) {
  const parts = [d.enseigne, d.ville].filter(Boolean);
  return parts.length ? `Projet ${parts.join(' ')}` : 'Projet';
}

// Date par défaut du retour visuel : J + 2 jours ouvrés (week-ends exclus)
function addBusinessDays(fromDate, n) {
  const d = new Date(fromDate);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return d;
}

function defaultRetourDate() {
  const d = addBusinessDays(new Date(), 2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Ouverture prévue par défaut : date de création + 8 semaines (56 jours)
function defaultOuvertureDate() {
  const d = new Date();
  d.setDate(d.getDate() + 56);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function CroquisAgenceurPage() {
  const { api, user, token } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const [params] = useSearchParams();
  const etudeId = params.get('etudeId');
  const [tab, setTab] = useState('demande');
  const [ataConfig, setAtaConfig] = useState({ role_config: {}, user_config: {} });
  const canCroquisTab = useCallback((t) => resolveTabVisible(ataConfig, user?.role, user?.id, 'croquis:' + t), [ataConfig, user]);
  useEffect(() => {
    api().get('/admin-tab-access/me')
      .then(r => setAtaConfig({ role_config: r.data?.role_config || {}, user_config: r.data?.user_config || {} }))
      .catch(() => {});
  }, [api]);
  const [catalogue, setCatalogue] = useState([]);
  const [modele, setModele] = useState({ L: 480, P: 310, items: [] });
  const [agenceurs, setAgenceurs] = useState([]);
  const [demande, setDemande] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [demandes, setDemandes] = useState([]);
  const [submittedList, setSubmittedList] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailEdited, setEmailEdited] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const saveTimer = useRef(null);

  const setField = (k, v) => setDemande((d) => ({ ...d, [k]: v }));
  const setItems = (fnOrArr) => setDemande((d) => ({ ...d, items: typeof fnOrArr === 'function' ? fnOrArr(d.items || []) : fnOrArr }));

  const blankDraft = useCallback(() => ({
    concept: ['Wasabi'], enseigne: 'Leclerc', adresse: '', cp: '', ville: '',
    typeContrat: 'Nouveau contrat', format: '4 faces', formatProjet: '', priorite: 'Standard',
    agenceurId: null, demandeurId: user?.id, retour: defaultRetourDate(), ouverture: defaultOuvertureDate(),
    L: 480, P: 310, hsp: null, items: [], statut: 'brouillon', indice: 'A',
    eq: {}, opts: [], reseaux: { evcorner: '', eau: '', elec: '' }, hab: [], notes: '',
  }), [user]);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState('');
  const [busy, setBusy] = useState('');
  const [confirmKind, setConfirmKind] = useState(null);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

  const fetchFiles = useCallback((id) => {
    if (!id) { setFiles([]); return; }
    api().get(`/croquis/demandes/${id}/files`).then((r) => setFiles(r.data.items || [])).catch(() => {});
  }, [api]);
  useEffect(() => { fetchFiles(demande?.id); }, [demande?.id, fetchFiles]);

  // Étape 5 : discussion + historique des envois
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [indicesList, setIndicesList] = useState([]);
  const [retourInd, setRetourInd] = useState('');
  const [uploadingRetour, setUploadingRetour] = useState(false);
  useEffect(() => {
    if (!demande?.id) { setComments([]); setIndicesList([]); return; }
    api().get(`/croquis/demandes/${demande.id}/comments`).then((r) => setComments(r.data.items || [])).catch(() => {});
    api().get(`/croquis/demandes/${demande.id}/indices`).then((r) => setIndicesList(r.data.items || [])).catch(() => {});
  }, [demande?.id, api, demande?.statut]);
  const sendComment = async () => {
    if (!newComment.trim() || !demande?.id) return;
    try { const r = await api().post(`/croquis/demandes/${demande.id}/comments`, { text: newComment }); setComments((c) => [...c, r.data]); setNewComment(''); } catch {}
  };
  const uploadRetour = async (fileList) => {
    if (!demande?.id || !fileList?.length) return;
    setUploadingRetour(true);
    try {
      const fd = new FormData(); fd.append('file', fileList[0]); fd.append('indice', retourInd);
      const r = await api().post(`/croquis/demandes/${demande.id}/retour`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDemande((d) => ({ ...d, statut: 'retour', indice: r.data.indice })); fetchFiles(demande.id);
      toast.success('Visuel de retour déposé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    finally { setUploadingRetour(false); }
  };
  const changeStatut = async (statut) => {
    if (!demande?.id) return;
    try { const r = await api().post(`/croquis/demandes/${demande.id}/status`, { statut }); setDemande(r.data); toast.success('Statut mis à jour'); } catch {}
  };

  // Recherche magasin (autocomplétion Google Places, comme les simulations CAPEX)
  const [storeQuery, setStoreQuery] = useState('');
  const [storeSug, setStoreSug] = useState([]);
  const [showStoreSug, setShowStoreSug] = useState(false);
  const acServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const storeDebounce = useRef(null);
  useEffect(() => {
    const tryInit = () => {
      if (window.google && window.google.maps && window.google.maps.places && !acServiceRef.current) {
        acServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
      }
    };
    tryInit();
    const t = setInterval(tryInit, 800);
    return () => clearInterval(t);
  }, []);
  const onStoreType = (value) => {
    if (storeDebounce.current) clearTimeout(storeDebounce.current);
    storeDebounce.current = setTimeout(() => {
      if (value.length < 3 || !acServiceRef.current) { setStoreSug([]); return; }
      acServiceRef.current.getPlacePredictions(
        { input: value, componentRestrictions: { country: 'fr' }, types: ['establishment', 'geocode'], language: 'fr' },
        (preds, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
            setStoreSug(preds.map((p) => ({ place_id: p.place_id, main_text: p.structured_formatting?.main_text || p.description, secondary_text: p.structured_formatting?.secondary_text || '' })));
            setShowStoreSug(true);
          } else setStoreSug([]);
        }
      );
    }, 300);
  };
  const selectStore = (sug) => {
    setShowStoreSug(false);
    setStoreQuery(`${sug.main_text} ${sug.secondary_text}`.trim());
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails({ placeId: sug.place_id, fields: ['address_components', 'formatted_address', 'name'] }, (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
      let num = '', route = '', cp = '', city = '';
      (place.address_components || []).forEach((c) => {
        if (c.types.includes('street_number')) num = c.long_name;
        if (c.types.includes('route')) route = c.long_name;
        if (c.types.includes('postal_code')) cp = c.long_name;
        if (c.types.includes('locality')) city = c.long_name;
      });
      const addr = [num, route].filter(Boolean).join(' ') || place.formatted_address || '';
      setDemande((d) => ({ ...d, adresse: addr, cp: cp || d.cp, ville: city || d.ville }));
    });
  };

  // Étape 6 : Réglages
  const [reglages, setReglages] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [agList, setAgList] = useState([]);
  const [dirRows, setDirRows] = useState([]); // [{id, email}] clés stables
  const [ccRows, setCcRows] = useState([]);   // [{id, role, userId, email}]
  const uid = () => 'r_' + Math.random().toString(36).slice(2, 9);
  const emailOk = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
  const initRows = (data) => {
    setDirRows((data.adminEmails || []).map((em) => ({ id: uid(), email: em })));
    setCcRows((data.copies || []).map((c) => ({ id: uid(), role: c.role || '', userId: c.userId || '', email: c.email || '' })));
  };
  useEffect(() => {
    if (tab !== 'reglages') return;
    api().get('/croquis/parametres').then((r) => { setReglages(r.data || {}); initRows(r.data || {}); }).catch(() => {});
    api().get('/croquis/users').then((r) => setUsersList(r.data.items || [])).catch(() => {});
    api().get('/croquis/agenceurs').then((r) => setAgList(r.data.items || [])).catch(() => {});
  }, [tab, api]);
  const saveAgenceur = async (ag) => {
    try { await api().put(`/croquis/agenceurs/${ag.id}`, ag); toast.success('Agenceur enregistré'); api().get('/croquis/agenceurs').then((r) => { setAgList(r.data.items || []); setAgenceurs(r.data.items || []); }); } catch { toast.error('Erreur'); }
  };
  const addAgenceur = async () => {
    try { await api().post('/croquis/agenceurs', { societe: 'DSAO', contact: '', email: '' }); api().get('/croquis/agenceurs').then((r) => { setAgList(r.data.items || []); setAgenceurs(r.data.items || []); }); toast.success('Agenceur ajouté'); } catch { toast.error('Réservé aux administrateurs'); }
  };
  const deleteAgenceur = async (id) => {
    try { await api().delete(`/croquis/agenceurs/${id}`); setAgList((l) => l.filter((x) => x.id !== id)); setAgenceurs((l) => l.filter((x) => x.id !== id)); toast.success('Agenceur supprimé'); } catch { toast.error('Erreur'); }
  };
  const saveReglages = async () => {
    const badDir = dirRows.some((r) => r.email && !emailOk(r.email));
    const badCc = ccRows.some((r) => r.email && !emailOk(r.email));
    const badExp = !emailOk((reglages.expediteur || {}).email);
    if (badDir || badCc || badExp) { toast.error('Corrigez les adresses email en rouge avant d\'enregistrer'); return; }
    const payload = {
      ...reglages,
      adminEmails: dirRows.map((r) => (r.email || '').trim()).filter(Boolean),
      copies: ccRows.map(({ id, ...r }) => r),
    };
    try { const r = await api().put('/croquis/parametres', payload); setReglages(r.data); initRows(r.data); toast.success('Réglages enregistrés'); } catch { toast.error('Réservé aux administrateurs'); }
  };

  useEffect(() => {
    Promise.all([
      api().get('/croquis/catalogue'),
      api().get('/croquis/agenceurs'),
    ]).then(([cat, ag]) => {
      setCatalogue(cat.data.catalogue || []);
      setModele(cat.data.modele_4faces || { L: 480, P: 310, items: [] });
      setAgenceurs(ag.data.items || []);
    }).catch(() => {});
    api().get('/croquis/parametres').then((r) => setReglages(r.data || {})).catch(() => {});
    // charge la demande liée à l'étude, sinon brouillon prérempli
    if (etudeId) {
      api().get(`/croquis/by-etude/${etudeId}`).then(async (r) => {
        if (r.data && r.data.id) { setDemande(r.data); return; }
        let pre = {};
        try { pre = (await api().get(`/croquis/prefill/${etudeId}`)).data || {}; } catch {}
        setDemande({ ...blankDraft(), etudeId, ...pre });
      }).catch(() => setDemande({ ...blankDraft(), etudeId }));
    } else {
      setDemande(blankDraft());
    }
  }, [api, etudeId, blankDraft]);

  const loadDemandes = useCallback(() => {
    api().get('/croquis/demandes').then((r) => setDemandes(r.data.items || [])).catch(() => {});
  }, [api]);
  useEffect(() => { if (tab === 'historique') loadDemandes(); }, [tab, loadDemandes]);

  const loadSubmitted = useCallback(() => {
    api().get('/croquis/demandes', { params: { statut: 'soumise' } }).then((r) => setSubmittedList(r.data.items || [])).catch(() => {});
  }, [api]);
  useEffect(() => { if (isAdmin && (tab === 'traiter' || tab === 'demande')) loadSubmitted(); }, [tab, isAdmin, loadSubmitted]);

  const save = async (silent) => {
    if (!demande) return;
    // §8.7 contrôles d'enregistrement
    if (!(demande.concept || []).length) { if (!silent) toast.error('Sélectionnez au moins un concept'); return; }
    if (!demande.ville) { if (!silent) toast.error('Renseignez le magasin / ville'); return; }
    if (!demande.formatProjet) { if (!silent) toast.error('Choisissez le format du projet'); return; }
    setSaving(true);
    try {
      let res;
      if (demande.id) res = await api().put(`/croquis/demandes/${demande.id}`, demande);
      else res = await api().post('/croquis/demandes', demande);
      setDemande(res.data);
      if (!silent) toast.success('Croquis enregistré');
      return res.data.id;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Erreur');
      return null;
    } finally { setSaving(false); }
  };

  // Sauvegarde auto toutes les 30 s si déjà enregistré
  useEffect(() => {
    if (!demande?.id) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(true), 30000);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [demande]);

  const loadModel = () => setDemande((d) => ({ ...d, L: modele.L, P: modele.P, items: (modele.items || []).map((it) => ({ ...it, id: 'it_' + Math.random().toString(36).slice(2, 8) })) }));
  const clearPlan = () => { setItems([]); setSelectedId(null); };

  const setEq = (key, field, val) => setDemande((d) => ({ ...d, eq: { ...(d.eq || {}), [key]: { ...((d.eq || {})[key] || {}), [field]: val } } }));
  const toggleHab = (group, lib) => {
    const val = `${group} : ${lib}`;
    setDemande((d) => { const h = d.hab || []; return { ...d, hab: h.includes(val) ? h.filter((x) => x !== val) : [...h, val] }; });
  };
  const setReseau = (k, v) => setDemande((d) => ({ ...d, reseaux: { ...(d.reseaux || {}), [k]: v } }));
  const toggleOpt = (o) => setDemande((d) => { const a = d.opts || []; return { ...d, opts: a.includes(o) ? a.filter((x) => x !== o) : [...a, o] }; });

  const reperes = (demande?.items || []).filter((i) => (catalogue.find((c) => c.k === i.k) || {}).type === 'repere')
    .map((i, idx) => ({ ...i, num: idx + 1 }));
  const setRepereTxt = (id, txt) => setItems((items) => items.map((i) => i.id === id ? { ...i, txt } : i));
  const addRepere = () => setItems((items) => [...items, { id: 'it_' + Math.random().toString(36).slice(2, 8), k: 'note', x: (demande.L || 480) / 2, y: (demande.P || 310) / 2, txt: '' }]);
  const removeItem = (id) => setItems((items) => items.filter((i) => i.id !== id));

  const ensureSaved = async () => {
    if (demande?.id) return demande.id;
    return await save(true);
  };

  // Capture EXACTE du croquis de l'éditeur (html2canvas) puis stockage pour l'intégrer tel quel au PDF
  const captureApercu = async (id) => {
    try {
      if (!id) return false;
      const el = document.querySelector('[data-testid="croquis-plan"]');
      if (!el) return false;
      const canvas = await html2canvas(el, { backgroundColor: '#e9e9e9', scale: 2, useCORS: true, logging: false });
      const dataUrl = canvas.toDataURL('image/png');
      await api().put(`/croquis/demandes/${id}/apercu`, { image: dataUrl });
      return true;
    } catch (e) {
      console.warn('captureApercu failed', e);
      return false;
    }
  };

  const doUpload = async (kind, fileList) => {
    let id = demande?.id;
    if (!id) {
      id = await save(true);
      if (!id) { toast.error("Renseignez concept, ville et format du projet, puis réessayez d'ajouter des fichiers"); return; }
    }
    setUploading(kind);
    try {
      for (const f0 of Array.from(fileList)) {
        const f = kind === 'photo' ? await compressImage(f0) : f0;
        const fd = new FormData(); fd.append('file', f); fd.append('kind', kind); fd.append('name', f0.name);
        await api().post(`/croquis/demandes/${id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      fetchFiles(id); toast.success('Fichier(s) ajouté(s)');
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi"); }
    finally { setUploading(''); }
  };
  const removeFile = async (fid) => { try { await api().delete(`/croquis/files/${fid}`); fetchFiles(demande.id); } catch {} };

  const openPdf = async () => {
    const pdfUrl = (id) => `${API_BASE}/croquis/demandes/${id}/pdf?auth=${encodeURIComponent(token)}`;
    // On ouvre un onglet tout de suite (évite le blocage popup), puis on enregistre + capture le croquis
    const win = window.open('', '_blank');
    const id = await ensureSaved();
    if (!id) { if (win) win.close(); toast.error('Enregistrez d\'abord la demande'); return; }
    await captureApercu(id);
    if (win) win.location.href = pdfUrl(id); else window.open(pdfUrl(id), '_blank');
  };

  const sendCroquis = async () => {
    if (!demande?.id) { toast.error('Enregistrez d\'abord la demande'); return; }
    setBusy('send');
    try {
      await captureApercu(demande.id);
      const override = emailEdited ? { subject: emailSubject, body: emailBody } : undefined;
      const r = await api().post(`/croquis/demandes/${demande.id}/send`, override);
      setDemande((d) => ({ ...d, statut: r.data.statut, sentAt: r.data.sentAt }));
      setEmailEdited(false);
      setShowSendModal(false);
      if (isAdmin) loadSubmitted();
      toast.success('Dossier envoyé à l\'agenceur');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de l\'envoi'); }
    finally { setBusy(''); }
  };

  const submitToAdmin = async () => {
    let id = demande?.id;
    if (!id) { id = await save(true); if (!id) { toast.error('Renseignez concept, ville et format du projet'); return; } }
    setBusy('submit');
    try {
      await captureApercu(id);
      const r = await api().post(`/croquis/demandes/${id}/submit`);
      setDemande((d) => ({ ...d, statut: r.data.statut, submittedAt: r.data.submittedAt }));
      if (r.data.emailSent) toast.success('Croquis envoyé à la Direction');
      else toast.success('Croquis envoyé à la Direction' + (r.data.adminEmails?.length ? '' : ' (aucun email admin configuré dans Réglages)'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de l\'envoi'); }
    finally { setBusy(''); }
  };

  const deleteCroquis = async () => {
    if (!demande?.id) { setDemande(blankDraft()); setConfirmKind(null); return; }
    try { await api().delete(`/croquis/demandes/${demande.id}`); toast.success('Croquis supprimé'); setDemande(blankDraft()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Suppression non autorisée'); }
    finally { setConfirmKind(null); }
  };

  const cancelChanges = async () => {
    if (demande?.id) { try { const r = await api().get(`/croquis/demandes/${demande.id}`); setDemande(r.data); } catch {} }
    else setDemande(blankDraft());
    setConfirmKind(null);
  };

  const newIndice = async () => {
    if (!demande?.id) return;
    try { const r = await api().post(`/croquis/demandes/${demande.id}/new-indice`); setDemande(r.data); toast.success(`Nouvel indice ${r.data.indice}`); } catch {}
  };

  const equip = useMemo(() => computeEquip(demande || {}, catalogue), [demande, catalogue]);
  const plans = files.filter((f) => f.kind === 'plan');
  const photos = files.filter((f) => f.kind === 'photo');
  const dwgs = files.filter((f) => f.kind === 'dwg');

  const selItem = (demande?.items || []).find((i) => i.id === selectedId) || null;
  const intent = useMemo(() => demande ? intentText(demande, catalogue) : '', [demande, catalogue]);

  // Champs sur carte blanche (espace de travail)
  const inp = 'w-full h-11 px-3 rounded-ctrl text-[15px] bg-surface border border-border text-ink focus:outline-none focus:ring-2 focus:ring-orange';
  const inpStyle = {};
  const lbl = 'block text-[11px] font-bold uppercase tracking-[0.08em] mb-1.5 text-text-2';
  const lblStyle = {};
  // Champs sur fond graphite (récap scène)
  const inpDark = 'w-full h-11 px-3 rounded-ctrl text-[15px] bg-graphite-800 border border-graphite-500 text-white placeholder:text-ondark-3 focus:outline-none focus:ring-2 focus:ring-orange';
  // Boutons
  const btnPrimary = 'inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-ctrl text-sm font-bold bg-orange text-orange-on transition-colors hover:brightness-105';
  const btnSecLight = 'inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-ctrl text-sm font-semibold bg-surface border border-border text-ink transition-colors hover:bg-surface-2';
  const btnSecDark = 'inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-ctrl text-sm font-semibold bg-transparent border border-graphite-400 text-white transition-colors hover:bg-graphite-700';
  const sectionNum = 'font-display font-bold text-orange-text text-xl leading-none';
  const sectionTitle = 'font-display font-bold uppercase tracking-[0.02em] text-ink text-[22px] leading-none';

  if (!demande) return <AppLayout><div className="p-8 text-text-3">Chargement…</div></AppLayout>;

  const googleSearch = () => {
    const q = (demande.ville ? `${demande.enseigne} ${demande.ville}` : demande.enseigne) || '';
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`, '_blank');
  };

  const parseAddress = (val) => {
    setField('adresse', val);
    const m = val.match(/(\d{5})\s+([A-Za-zÀ-ÿ' -]+?)(?:,?\s*France)?\s*$/);
    if (m) { setField('cp', m[1]); setField('ville', m[2].trim()); }
  };

  const tabOptions = [
    canCroquisTab('demande') && { value: 'demande', label: 'Demande' },
    canCroquisTab('traiter') && { value: 'traiter', label: `À traiter${submittedList.length ? ` (${submittedList.length})` : ''}` },
    canCroquisTab('historique') && { value: 'historique', label: `${isAdmin ? 'Suivi & historique' : 'Historique'}${demandes.length ? ` (${demandes.length})` : ''}` },
    canCroquisTab('reglages') && { value: 'reglages', label: 'Réglages' },
  ].filter(Boolean);

  // Données e-mail (déplacées dans la fenêtre de confirmation §5.3.5)
  const ag = agenceurs.find((a) => a.id === demande.agenceurId);
  const exp = (reglages && reglages.expediteur) || {};
  const ccList = ((reglages && reglages.copies) || []).map((c) => `${c.role} : ${c.email || 'email à compléter dans Réglages'}`);
  const defSubject = `Demande de visuel — ${projectTitle(demande)} — ${demande.typeContrat || ''}${(demande.priorite || '').toLowerCase().startsWith('urgent') ? ' — URGENT' : ''}`;
  const cmt = (demande.commentaireCreateur || '').trim();
  const defBody = `Bonjour,\n\nVous trouverez dans ce dossier les éléments pour assurer ce nouveau projet.\n${cmt ? `\nCommentaire du demandeur :\n${cmt}\n` : ''}\nRestant à votre disposition si besoin.\n\n${exp.nom || ''}`.trim();
  const pjName = `Demande_visuel_${(demande.enseigne || '').replace(/\s/g, '')}_${(demande.ville || '').replace(/\s/g, '')}_IND_${demande.indice || 'A'}.pdf`;
  const subjVal = emailEdited ? emailSubject : defSubject;
  const bodyVal = emailEdited ? emailBody : defBody;
  const startEmailEdit = () => { if (!emailEdited) { setEmailSubject(defSubject); setEmailBody(defBody); setEmailEdited(true); } };

  // Frise de suivi §5.3.4 (barres)
  const steps = [
    { code: 'brouillon', label: 'Brouillon' },
    { code: 'soumise', label: 'Soumis' },
    { code: 'envoyee', label: "Chez l'agenceur" },
    { code: 'validee', label: 'Validé' },
  ];
  const idxByStatut = { brouillon: 0, soumise: 1, envoyee: 2, retour: 2, modifs: 2, validee: 3 };
  const curStep = idxByStatut[demande.statut] ?? 0;

  return (
    <AppLayout>
      <div className="max-w-[1280px] space-y-6" data-testid="croquis-page">
        {/* En-tête §5.3.1 */}
        <PageHeader overline="Projets corners · Demande de visuel à l'agenceur" title="CROQUIS AGENCEUR">
          <Segmented value={tab} onChange={setTab} options={tabOptions} />
          <button onClick={() => { setDemande(blankDraft()); setEmailEdited(false); setTab('demande'); }} data-testid="croquis-new" className={btnSecLight}>
            <Plus className="w-4 h-4" /> Nouvelle demande
          </button>
        </PageHeader>

        {tab === 'demande' && (
          <>
            {etudeId && (
              <div className="px-4 py-3 rounded-ctrl text-sm bg-orange-tint text-orange-tint-text" data-testid="croquis-prefill-banner">
                Demande pré-remplie depuis l'étude CAPEX {demande.capexRef || etudeId}. Vérifiez et complétez avant envoi.
              </div>
            )}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Colonne gauche : formulaire */}
              <div className="flex-[2_1_520px] min-w-0 space-y-6">
                {/* Bloc 01 Projet */}
                <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="croquis-bloc-projet">
                  <div className="flex items-center gap-2 mb-4"><span className={sectionNum}>01</span><h2 className={sectionTitle}>Projet</h2></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={lbl}>Concept (choix multiple)</label>
                      <div className="flex flex-wrap gap-2">
                        {CONCEPTS.map((c) => {
                          const on = (demande.concept || []).includes(c);
                          return <button key={c} onClick={() => setField('concept', on ? demande.concept.filter((x) => x !== c) : [...(demande.concept || []), c])}
                            data-testid={`croquis-concept-${c}`} className={`h-10 px-4 rounded-full text-sm font-semibold transition-colors ${on ? 'bg-graphite-900 text-white' : 'bg-surface border border-border text-text-2 hover:bg-surface-2'}`}>{c}</button>;
                        })}
                      </div>
                    </div>
                    <div>
                      <label className={lbl}>Enseigne</label>
                      <select className={inp} value={demande.enseigne || ''} onChange={(e) => setField('enseigne', e.target.value)} data-testid="croquis-enseigne">
                        {ENSEIGNES.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={lbl}>Rechercher le magasin</label>
                      <div className="flex gap-2 relative">
                        <input className={inp} placeholder="ex. Leclerc Marmande" data-testid="croquis-search-store"
                          value={storeQuery} autoComplete="off"
                          onChange={(e) => { setStoreQuery(e.target.value); onStoreType(e.target.value); }}
                          onFocus={() => storeSug.length && setShowStoreSug(true)} />
                        <button onClick={googleSearch} className={`${btnSecLight} whitespace-nowrap`}><Search className="w-4 h-4" /> Google</button>
                        {showStoreSug && storeSug.length > 0 && (
                          <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-ctrl overflow-hidden shadow-2xl bg-surface border border-border" data-testid="croquis-store-suggestions">
                            {storeSug.map((s) => (
                              <button key={s.place_id} onClick={() => selectStore(s)} className="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-surface-2 border-b border-divider" data-testid="croquis-store-sug">
                                <span className="font-semibold">{s.main_text}</span> <span className="text-text-3">{s.secondary_text}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={lbl}>Adresse du magasin</label>
                      <input className={inp} value={demande.adresse || ''} onChange={(e) => parseAddress(e.target.value)} data-testid="croquis-adresse" />
                    </div>
                    <div>
                      <label className={lbl}>Code postal</label>
                      <input className={inp} value={demande.cp || ''} onChange={(e) => setField('cp', e.target.value)} data-testid="croquis-cp" />
                    </div>
                    <div>
                      <label className={lbl}>Ville</label>
                      <input className={inp} value={demande.ville || ''} onChange={(e) => setField('ville', e.target.value)} data-testid="croquis-ville" />
                    </div>
                    <div>
                      <label className={lbl}>Type de projet</label>
                      <select className={inp} value={demande.typeContrat || ''} onChange={(e) => setField('typeContrat', e.target.value)} data-testid="croquis-type">
                        {TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Implantation</label>
                      <select className={inp} value={demande.format || ''} onChange={(e) => setField('format', e.target.value)} data-testid="croquis-implantation">
                        {IMPLANTATIONS.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Format du projet</label>
                      <select className={inp} value={demande.formatProjet || ''} onChange={(e) => setField('formatProjet', e.target.value)} data-testid="croquis-format">
                        <option value="">Choisir…</option>
                        {FORMATS.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Agenceur destinataire</label>
                      <select className={inp} value={demande.agenceurId || ''} onChange={(e) => setField('agenceurId', e.target.value)} data-testid="croquis-agenceur">
                        <option value="">Choisir…</option>
                        {agenceurs.map((a) => <option key={a.id} value={a.id}>{a.societe} — {a.contact} · {a.email || 'email à compléter'}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Priorité</label>
                      <select className={inp} value={demande.priorite || ''} onChange={(e) => setField('priorite', e.target.value)} data-testid="croquis-priorite">
                        {PRIORITES.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Retour visuel souhaité</label>
                      <input type="date" className={inp} value={demande.retour || ''} onChange={(e) => setField('retour', e.target.value)} data-testid="croquis-retour" />
                    </div>
                    <div>
                      <label className={lbl}>Ouverture prévue</label>
                      <input type="date" className={inp} value={demande.ouverture || ''} onChange={(e) => setField('ouverture', e.target.value)} data-testid="croquis-ouverture" />
                    </div>
                  </div>
                </div>

                {/* Bloc 02 Documents du site — stockage objet */}
                <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="croquis-bloc-docs">
                  <div className="flex items-center gap-2 mb-4"><span className={sectionNum}>02</span><h2 className={sectionTitle}>Documents du site</h2></div>
                  {!demande.id && <p className="text-xs mb-3 text-orange-text">Enregistrez la demande pour activer l'ajout de fichiers.</p>}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { kind: 'plan', label: 'Plan magasin (PDF)', accept: 'application/pdf', list: plans },
                      { kind: 'photo', label: 'Photos emplacement', accept: 'image/*', list: photos },
                      { kind: 'dwg', label: 'Plan DWG', accept: '.dwg', list: dwgs },
                    ].map((z) => (
                      <div key={z.kind} className="rounded-ctrl p-4 border border-dashed border-[#CFCBC2] bg-[#FAF9F6]">
                        <div className="text-[13px] font-semibold text-ink mb-3">{z.label}</div>
                        <label data-testid={`croquis-upload-${z.kind}`} className="inline-flex items-center gap-1 h-9 px-3 rounded-ctrl text-xs font-semibold cursor-pointer bg-surface border border-border text-ink hover:bg-surface-2">
                          {uploading === z.kind ? 'Envoi…' : '+ Ajouter'}
                          <input type="file" multiple={z.kind !== 'dwg'} accept={z.accept} className="hidden" disabled={uploading === z.kind}
                            onChange={(e) => { if (e.target.files?.length) doUpload(z.kind, e.target.files); e.target.value = ''; }} />
                        </label>
                        <div className="mt-3 space-y-1">
                          {z.list.map((f) => (
                            <div key={f.id} className="flex items-center justify-between text-[12px] px-2 py-1.5 rounded-md bg-surface-2" data-testid={`croquis-file-${f.id}`}>
                              <a href={`${API_BASE}/croquis/files/${f.id}/download?auth=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" className="truncate mr-2 text-text-2 hover:text-ink">{f.name}</a>
                              <button onClick={() => removeFile(f.id)} data-testid={`croquis-file-del-${f.id}`} aria-label="Supprimer le fichier" className="text-danger"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                          {z.list.length === 0 && <div className="text-[11px] text-text-3">Aucun fichier</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bloc 03 Emprise & croquis */}
                <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="croquis-bloc-emprise">
                  <div className="flex items-center gap-2 mb-4"><span className={sectionNum}>03</span><h2 className={sectionTitle}>Emprise &amp; croquis</h2></div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div><label className={lbl}>Largeur (cm)</label><input type="number" className={inp} value={demande.L} onChange={(e) => setField('L', Number(e.target.value) || 0)} data-testid="croquis-L" /></div>
                    <div><label className={lbl}>Profondeur (cm)</label><input type="number" className={inp} value={demande.P} onChange={(e) => setField('P', Number(e.target.value) || 0)} data-testid="croquis-P" /></div>
                    <div><label className={lbl}>Hauteur sous plafond (cm)</label><input type="number" className={inp} value={demande.hsp || ''} onChange={(e) => setField('hsp', Number(e.target.value) || null)} data-testid="croquis-hsp" /></div>
                  </div>
                  <CroquisEditor items={demande.items || []} L={demande.L} P={demande.P} catalogue={catalogue}
                    selectedId={selectedId} onChange={setItems} onSelect={setSelectedId} onLoadModel={loadModel} onClear={clearPlan} />
                  <SelectionBar item={selItem} catalogue={catalogue} onChange={setItems} onSelect={setSelectedId} maxLen={demande.L} />
                </div>

                {/* Bloc 04 Équipements attendus */}
                <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="croquis-bloc-equip">
                  <div className="flex items-center gap-2 mb-4"><span className={sectionNum}>04</span><h2 className={sectionTitle}>Équipements attendus</h2></div>
                  {equip.length === 0 ? <p className="text-sm text-text-3">Ajoutez des meubles sur le croquis pour alimenter le tableau.</p> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="croquis-equip-table">
                        <thead><tr className="text-[11px] font-bold uppercase tracking-[0.1em] text-label border-b border-[#E6E4DE]"><th className="text-left py-2">Équipement</th><th className="py-2">Qté</th><th className="py-2">Cotes</th><th className="text-left py-2">Statut</th><th className="text-left py-2">Précision pour l'agenceur</th></tr></thead>
                        <tbody>
                          {equip.map((g) => (
                            <tr key={g.key} className="border-b border-divider">
                              <td className="py-2 pr-2 text-ink">{g.libelle}</td>
                              <td className="text-center text-text-2 tabular-nums">{g.qte}</td>
                              <td className="text-center whitespace-nowrap text-text-2 tabular-nums">{g.L}×{g.P}</td>
                              <td className="pr-2 py-1.5">
                                <select value={g.statut} onChange={(e) => setEq(g.key, 'statut', e.target.value)} data-testid={`croquis-eq-statut-${g.key}`} className="h-9 px-2 rounded-ctrl text-xs bg-surface border border-border text-ink">
                                  {EQ_STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </td>
                              <td className="py-1.5"><input value={g.precision} onChange={(e) => setEq(g.key, 'precision', e.target.value)} data-testid={`croquis-eq-prec-${g.key}`} placeholder="ex. porte à droite" className="w-full h-9 px-2 rounded-ctrl text-xs bg-surface border border-border text-ink" /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Bloc 05 Options techniques */}
                <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="croquis-bloc-options">
                  <div className="flex items-center gap-2 mb-4"><span className={sectionNum}>05</span><h2 className={sectionTitle}>Options techniques</h2></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <label className="flex items-center gap-2 cursor-pointer text-text-2" data-testid="croquis-opt-demontage">
                      <input type="checkbox" className="accent-[#F26A1B] w-4 h-4" checked={(demande.opts || []).includes('demontage')} onChange={() => toggleOpt('demontage')} /> Démontage de l'ancien corner
                    </label>
                    {[
                      { k: 'evcorner', label: 'Évacuation du corner' },
                      { k: 'eau', label: "Arrivée d'eau existante" },
                      { k: 'elec', label: 'Arrivée électrique existante' },
                    ].map((row) => (
                      <div key={row.k} className="flex items-center justify-between gap-2">
                        <span className="text-text-2">{row.label}</span>
                        <div className="flex gap-1">
                          {['oui', 'non'].map((v) => (
                            <button key={v} onClick={() => setReseau(row.k, v)} data-testid={`croquis-res-${row.k}-${v}`} className={`h-9 px-3 rounded-ctrl text-xs font-semibold capitalize transition-colors ${(demande.reseaux || {})[row.k] === v ? 'bg-graphite-900 text-white' : 'bg-surface border border-border text-text-2 hover:bg-surface-2'}`}>{v}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(demande.reseaux || {}).eau === 'oui' && !(demande.items || []).some((i) => i.k === 'eau') && (
                    <p className="text-xs mt-3 text-orange-text">Positionnez l'arrivée d'eau sur le croquis.</p>
                  )}
                  {(demande.reseaux || {}).elec === 'oui' && !(demande.items || []).some((i) => i.k === 'elec') && (
                    <p className="text-xs mt-1 text-orange-text">Positionnez l'arrivée électrique sur le croquis.</p>
                  )}
                </div>

                {/* Bloc 06 Habillage & signalétique */}
                <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="croquis-bloc-habillage">
                  <div className="flex items-center gap-2 mb-4"><span className={sectionNum}>06</span><h2 className={sectionTitle}>Habillage &amp; signalétique</h2></div>
                  <div className="space-y-4">
                    {Object.entries(HAB_GROUPS).map(([group, opts]) => (
                      <div key={group}>
                        <div className="text-[11px] font-bold uppercase tracking-[0.08em] mb-2 text-label">{group}</div>
                        <div className="flex flex-wrap gap-2">
                          {opts.map((o) => {
                            const on = (demande.hab || []).includes(`${group} : ${o}`);
                            return <button key={o} onClick={() => toggleHab(group, o)} data-testid={`croquis-hab-${group}-${o}`} className={`h-9 px-3 rounded-ctrl text-xs font-semibold transition-colors ${on ? 'bg-graphite-900 text-white' : 'bg-surface border border-border text-text-2 hover:bg-surface-2'}`}>{o}</button>;
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bloc 07 Points d'attention */}
                <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="croquis-bloc-attention">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2"><span className={sectionNum}>07</span><h2 className={sectionTitle}>Points d'attention</h2></div>
                    <button onClick={addRepere} data-testid="croquis-add-repere" className="inline-flex items-center gap-1 h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-ink hover:bg-surface-2"><Plus className="w-3.5 h-3.5" /> Ajouter un repère</button>
                  </div>
                  {reperes.length === 0 ? <p className="text-sm text-text-3">Aucun repère. Ajoutez une épingle sur le croquis.</p> : (
                    <div className="space-y-2">
                      {reperes.map((r) => (
                        <div key={r.id} className="flex items-center gap-2" data-testid={`croquis-repere-${r.num}`}>
                          <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-graphite-900 text-white shrink-0">{r.num}</span>
                          <input value={r.txt || ''} onChange={(e) => setRepereTxt(r.id, e.target.value)} placeholder={`Décrivez le point d'attention n° ${r.num}`} className={inp} />
                          <button onClick={() => removeItem(r.id)} aria-label="Retirer le repère" className="text-danger text-sm font-semibold px-2 shrink-0">Retirer</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4">
                    <label className={lbl}>Remarques générales</label>
                    <textarea value={demande.notes || ''} onChange={(e) => setField('notes', e.target.value)} rows={2} className={`${inp} h-auto py-2`} data-testid="croquis-notes" />
                  </div>
                </div>
              </div>

              {/* Colonne droite : récapitulatif (bloc scène graphite §5.3.4) */}
              <div className="flex-[1_1_320px] min-w-0 space-y-6">
                <div className="rounded-scene bg-graphite-900 p-6 sticky top-4" data-testid="croquis-synthese">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1">Client</div>
                      <div className="font-display font-bold uppercase text-[26px] leading-[1.05] text-white truncate">{projectTitle(demande) || '—'}</div>
                      <div className="text-sm font-semibold text-orange mt-1" data-testid="croquis-projet-name">{projetName(demande)}</div>
                    </div>
                    <div className="shrink-0 w-[52px] h-[52px] rounded-ctrl bg-graphite-800 border border-graphite-600 flex items-center justify-center">
                      <span className="font-display font-bold text-[26px] text-orange leading-none">{demande.indice || 'A'}</span>
                    </div>
                  </div>

                  {/* Frise 4 étapes en barres */}
                  <div className="grid grid-cols-4 gap-1.5 mb-2" data-testid="croquis-statut-frise">
                    {steps.map((s, i) => (
                      <div key={s.code} className="h-1.5 rounded-full" style={{ background: i === curStep ? '#F26A1B' : i < curStep ? '#54565E' : '#3A3C42' }} />
                    ))}
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 mb-4">
                    {steps.map((s, i) => (
                      <span key={s.code} className={`text-[10px] leading-tight ${i === curStep ? 'text-orange font-semibold' : 'text-ondark-3'}`}>{s.label}</span>
                    ))}
                  </div>

                  <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1">En une phrase</div>
                  <p className="text-sm leading-relaxed mb-4 text-ondark-2" data-testid="croquis-intent">{intent}</p>

                  {equip.length > 0 && (
                    <>
                      <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1">Équipements</div>
                      <div className="text-[13px] mb-4 text-ondark-2 space-y-0.5">
                        {equip.map((g) => <div key={g.key}>{g.qte} × {g.libelle}{g.statut !== 'Neuf' ? ` (${g.statut})` : ''}</div>)}
                      </div>
                    </>
                  )}
                  {reperes.length > 0 && (
                    <>
                      <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1">Points d'attention</div>
                      <div className="text-[13px] mb-4 text-ondark-2 space-y-0.5">{reperes.map((r) => <div key={r.id}>{r.num}. {r.txt || '—'}</div>)}</div>
                    </>
                  )}

                  <div className="space-y-1.5 py-3 border-t border-graphite-600">
                    <div className="text-sm flex justify-between"><span className="text-ondark-3">Retour visuel</span><span className="text-white tabular-nums">{frDate(demande.retour) || '—'}</span></div>
                    <div className="text-sm flex justify-between"><span className="text-ondark-3">Ouverture</span><span className="text-white tabular-nums">{frDate(demande.ouverture) || '—'}</span></div>
                    <div className="text-sm flex justify-between"><span className="text-ondark-3">Pièces jointes</span><span className="text-white tabular-nums">{plans.length} plan · {photos.length} photo · {dwgs.length} DWG</span></div>
                  </div>

                  <div className="pt-3 border-t border-graphite-600">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1.5">Commentaire (détails supplémentaires)</div>
                    <textarea value={demande.commentaireCreateur || ''} onChange={(e) => setField('commentaireCreateur', e.target.value)} rows={2} placeholder="Précisions transmises à l'admin et à l'agenceur…" className={`${inpDark} h-auto py-2`} data-testid="croquis-commentaire" />
                  </div>

                  <div className="mt-4 space-y-2">
                    {/* Bouton principal orange §5.3.4 / §5.3.5 */}
                    {isAdmin ? (
                      <button onClick={() => { startEmailEdit(); setShowSendModal(true); }} data-testid="croquis-open-send" className={`${btnPrimary} w-full`}>
                        <Send className="w-4 h-4" /> Envoyer à l'agenceur
                      </button>
                    ) : (
                      <button onClick={submitToAdmin} disabled={busy === 'submit'} data-testid="croquis-submit-admin" className={`${btnPrimary} w-full`}>
                        <Send className="w-4 h-4" /> {busy === 'submit' ? 'Envoi…' : 'Envoyer croquis à la Direction'}
                      </button>
                    )}

                    {/* Boutons secondaires sur graphite */}
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => save(false)} disabled={saving} data-testid="croquis-save" className={btnSecDark}>
                        <Save className="w-4 h-4" /> {saving ? '…' : 'Enregistrer'}
                      </button>
                      <button onClick={openPdf} data-testid="croquis-pdf" className={btnSecDark}>
                        <FileText className="w-4 h-4" /> Dossier PDF
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setConfirmKind('cancel')} data-testid="croquis-cancel" className="h-11 px-4 rounded-ctrl text-sm font-semibold bg-transparent border border-graphite-400 text-ondark-2 hover:bg-graphite-700 transition-colors">{isAdmin ? 'Annuler' : 'Annuler le brouillon'}</button>
                      <button onClick={() => setConfirmKind('delete')} data-testid="croquis-delete" className="h-11 px-4 rounded-ctrl text-sm font-semibold bg-transparent border border-graphite-400 text-danger hover:bg-graphite-700 transition-colors">{isAdmin ? 'Supprimer' : 'Supprimer le brouillon'}</button>
                    </div>
                    {demande.statut === 'envoyee' && (
                      <button onClick={newIndice} data-testid="croquis-new-indice" className="w-full h-10 px-4 rounded-ctrl text-xs font-semibold bg-transparent border border-graphite-400 text-ondark-2 hover:bg-graphite-700 transition-colors">Préparer un nouvel indice</button>
                    )}
                    {confirmKind === 'cancel' && (
                      <div className="text-xs p-3 rounded-ctrl bg-graphite-800 text-ondark-2" data-testid="croquis-confirm-cancel">
                        Abandonner les modifications non enregistrées ?
                        <div className="flex gap-2 mt-2"><button onClick={cancelChanges} className="h-8 px-3 rounded-ctrl text-xs font-bold bg-orange text-orange-on">Oui</button><button onClick={() => setConfirmKind(null)} className="h-8 px-3 rounded-ctrl text-xs bg-graphite-700 text-white">Non</button></div>
                      </div>
                    )}
                    {confirmKind === 'delete' && (
                      <div className="text-xs p-3 rounded-ctrl bg-graphite-800 text-danger" data-testid="croquis-confirm-delete">
                        Supprimer définitivement ce croquis et son historique ?
                        <div className="flex gap-2 mt-2"><button onClick={deleteCroquis} className="h-8 px-3 rounded-ctrl text-xs font-bold text-white" style={{ background: '#B42318' }}>Oui</button><button onClick={() => setConfirmKind(null)} className="h-8 px-3 rounded-ctrl text-xs bg-graphite-700 text-white">Non</button></div>
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] mt-3 text-ondark-3">{demande.ref ? `Demande ${demande.ref} · ${demande.statut}${demande.sentAt ? ' · envoyée le ' + frDate(demande.sentAt) : ''}` : 'Nouvelle demande, non enregistrée'}</div>
                </div>

                {/* Retours agenceur & discussion — carte de travail blanche */}
                <div className="rounded-card bg-surface p-5" data-testid="croquis-retours">
                  <div className="font-display font-bold uppercase tracking-[0.02em] text-ink text-lg mb-3">Retour agenceur</div>
                  {!demande.id ? <p className="text-sm text-text-3">Enregistrez la demande pour gérer les retours.</p> : (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <input value={retourInd} onChange={(e) => setRetourInd(e.target.value.toUpperCase().slice(0, 1))} placeholder="IND" className="w-14 h-10 px-2 rounded-ctrl text-sm text-center bg-surface border border-border text-ink" data-testid="croquis-retour-indice" />
                        <label data-testid="croquis-retour-upload" className="flex-1 text-center h-10 inline-flex items-center justify-center px-3 rounded-ctrl text-xs font-semibold cursor-pointer bg-surface border border-border text-ink hover:bg-surface-2">
                          {uploadingRetour ? 'Dépôt…' : 'Déposer le visuel de retour (PDF)'}
                          <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { if (e.target.files?.length) uploadRetour(e.target.files); e.target.value = ''; }} />
                        </label>
                      </div>
                      <div className="flex gap-2 mb-3">
                        <button onClick={() => changeStatut('modifs')} data-testid="croquis-statut-modifs" className="flex-1 h-9 px-2 rounded-ctrl text-xs font-semibold bg-surface border border-border text-text-2 hover:bg-surface-2">Modifications demandées</button>
                        <button onClick={() => changeStatut('validee')} data-testid="croquis-statut-validee" className="flex-1 h-9 px-2 rounded-ctrl text-xs font-semibold bg-graphite-900 text-white hover:bg-graphite-700">Validée</button>
                      </div>
                      {files.filter((f) => f.kind === 'retour').map((f) => (
                        <a key={f.id} href={`${API_BASE}/croquis/files/${f.id}/download?auth=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" className="block text-xs mb-1 text-orange-text font-medium">↳ {f.name}</a>
                      ))}
                    </>
                  )}
                  {indicesList.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-divider">
                      <div className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1.5 text-label">Historique des envois</div>
                      {indicesList.map((s, i) => (
                        <div key={i} className="text-xs flex justify-between text-text-2"><span>IND {s.indice} → {s.to}</span><span className="text-text-3 tabular-nums">{frDate(s.sentAt)}</span></div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-t border-divider">
                    <div className="text-[11px] font-bold uppercase tracking-[0.08em] mb-2 text-label">Discussion interne</div>
                    <div className="space-y-1.5 max-h-40 overflow-auto mb-2" data-testid="croquis-comments">
                      {comments.length === 0 ? <div className="text-xs text-text-3">Aucun message.</div> : comments.map((c) => (
                        <div key={c.id} className="text-xs px-2 py-1.5 rounded-md bg-surface-2">
                          <span className="font-semibold text-ink">{c.author}</span> <span className="text-text-3">{frDate(c.at)}</span><div className="text-text-2">{c.text}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendComment()} placeholder="Votre message…" className="flex-1 h-10 px-3 rounded-ctrl text-sm bg-surface border border-border text-ink" data-testid="croquis-comment-input" disabled={!demande.id} />
                      <button onClick={sendComment} data-testid="croquis-comment-send" className="inline-flex items-center justify-center h-10 px-4 rounded-ctrl text-sm font-semibold bg-graphite-900 text-white transition-colors hover:bg-graphite-700 disabled:opacity-50" disabled={!demande.id}>Envoyer</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {tab === 'traiter' && canCroquisTab('traiter') && (
          <div className="rounded-card bg-surface p-5" data-testid="croquis-traiter">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold uppercase tracking-[0.02em] text-ink text-lg">Demandes soumises à traiter <span className="text-orange-text">({submittedList.length})</span></div>
              <button onClick={loadSubmitted} data-testid="croquis-traiter-refresh" className="h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-ink hover:bg-surface-2">Rafraîchir</button>
            </div>
            {submittedList.length === 0 ? <p className="text-sm text-text-3">Aucune demande en attente.</p> : (
              <div className="space-y-2">
                {submittedList.map((d) => {
                  const days = d.submittedAt ? Math.floor((Date.now() - new Date(d.submittedAt).getTime()) / 86400000) : 0;
                  const late = days >= 5;
                  return (
                    <button key={d.id} onClick={() => { setDemande(d); setEmailEdited(false); setTab('demande'); }} data-testid={`croquis-traiter-${d.id}`}
                      className={`w-full text-left px-4 py-3 rounded-ctrl flex items-center justify-between gap-3 bg-surface border transition-colors hover:bg-surface-2 ${late ? 'border-orange' : 'border-border'}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-ink truncate">{d.ref} · {projetName(d)}</div>
                        <div className="text-xs text-text-3">Par {d.createdByName || '—'} · {d.typeContrat} · IND {d.indice}{d.commentaireCreateur ? ' · commentaire' : ''}</div>
                      </div>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-[6px] flex-shrink-0 bg-orange-tint text-orange-tint-text">{late ? `${days} j — en retard` : `soumise · ${days} j`}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'historique' && (
          isAdmin ? (
            <CroquisAdminSuivi demandes={demandes} agenceurs={agenceurs} onReload={loadDemandes}
              pdfUrl={(id) => `${API_BASE}/croquis/demandes/${id}/pdf?auth=${encodeURIComponent(token)}`}
              onEdit={(d) => { setDemande(d); setTab('demande'); }} />
          ) : (
          <div className="rounded-card bg-surface p-5" data-testid="croquis-historique">
            {demandes.length === 0 ? <p className="text-sm text-text-3">Aucune demande enregistrée.</p> : (
              <div className="space-y-2">
                {demandes.map((d) => (
                  <button key={d.id} onClick={() => { setDemande(d); setTab('demande'); }} data-testid={`croquis-hist-${d.id}`}
                    className="w-full text-left px-4 py-3 rounded-ctrl flex items-center justify-between bg-surface border border-border transition-colors hover:bg-surface-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{d.ref} · {projectTitle(d)}</div>
                      <div className="text-xs text-text-3">{d.typeContrat} · {d.L}×{d.P} cm · IND {d.indice}</div>
                    </div>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-[6px] bg-surface-2 text-[#2E2D2A] shrink-0">{d.statut}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          )
        )}

        {tab === 'reglages' && canCroquisTab('reglages') && (
          <div className="space-y-6" data-testid="croquis-reglages">
            {reglages === null ? <p className="text-sm text-text-3">Chargement…</p> : (
              <>
                {/* DIRECTION */}
                <div className="rounded-card bg-surface p-5 sm:p-6">
                  <h2 className="font-display font-bold uppercase tracking-[0.02em] text-ink text-lg mb-1">Direction</h2>
                  <p className="text-xs mb-3 text-text-3">Emails notifiés à chaque nouvelle demande envoyée par un utilisateur, et destinataires des relances.</p>
                  <div className="space-y-2" data-testid="croquis-direction-list">
                    {dirRows.map((row, i) => {
                      const bad = row.email && !emailOk(row.email);
                      return (
                      <div key={row.id} className="flex gap-2 items-center" data-testid={`croquis-direction-row-${i}`}>
                        <div className="flex-1">
                          <input value={row.email} onChange={(e) => setDirRows((l) => l.map((x) => x.id === row.id ? { ...x, email: e.target.value } : x))} placeholder="direction@wasabi.fr" className={inp} style={bad ? { borderColor: '#B42318' } : {}} data-testid={`croquis-direction-email-${i}`} />
                          {bad && <span className="text-[10px] text-danger">Adresse invalide</span>}
                        </div>
                        <button onClick={() => setDirRows((l) => l.filter((x) => x.id !== row.id))} data-testid={`croquis-direction-del-${i}`} aria-label="Supprimer l'adresse" className="h-11 px-3 rounded-ctrl bg-surface border border-border text-danger hover:bg-surface-2"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      );
                    })}
                    {dirRows.length === 0 && <p className="text-xs text-text-3">Aucune adresse — ajoutez-en une.</p>}
                  </div>
                  <button onClick={() => setDirRows((l) => [...l, { id: uid(), email: '' }])} data-testid="croquis-direction-add" className="mt-3 inline-flex items-center gap-1 h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-ink hover:bg-surface-2"><Plus className="w-3.5 h-3.5" /> Ajouter une adresse</button>
                </div>

                {/* AGENCEURS */}
                <div className="rounded-card bg-surface p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display font-bold uppercase tracking-[0.02em] text-ink text-lg">Agenceurs</h2>
                    <button onClick={addAgenceur} data-testid="croquis-ag-add" className="inline-flex items-center gap-1 h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-ink hover:bg-surface-2"><Plus className="w-3.5 h-3.5" /> Ajouter un agenceur</button>
                  </div>
                  <div className="space-y-3">
                    {agList.map((ag, idx) => (
                      <div key={ag.id} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid={`croquis-ag-${ag.id}`}>
                        <div><label className={lbl}>Société</label><input value={ag.societe || ''} onChange={(e) => setAgList((l) => l.map((x, i) => i === idx ? { ...x, societe: e.target.value } : x))} className={inp} /></div>
                        <div><label className={lbl}>Contact</label><input value={ag.contact || ''} onChange={(e) => setAgList((l) => l.map((x, i) => i === idx ? { ...x, contact: e.target.value } : x))} className={inp} /></div>
                        <div><label className={lbl}>Profil app (email)</label>
                          <select value={ag.userId || ''} onChange={(e) => { const u = usersList.find((x) => x.id === e.target.value); setAgList((l) => l.map((x, i) => i === idx ? { ...x, userId: e.target.value, email: u?.email || x.email } : x)); }} className={inp} data-testid={`croquis-ag-user-${ag.id}`}>
                            <option value="">— email manuel —</option>
                            {usersList.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
                          </select>
                        </div>
                        <div><label className={lbl}>Email</label><input value={ag.email || ''} onChange={(e) => setAgList((l) => l.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))} placeholder="email" className={inp} /></div>
                        <div className="flex gap-2">
                          <button onClick={() => saveAgenceur(agList[idx])} data-testid={`croquis-ag-save-${ag.id}`} className={`${btnPrimary} flex-1`}>OK</button>
                          <button onClick={() => deleteAgenceur(ag.id)} data-testid={`croquis-ag-del-${ag.id}`} aria-label="Supprimer l'agenceur" className="h-11 px-3 rounded-ctrl bg-surface border border-border text-danger hover:bg-surface-2"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                    {agList.length === 0 && <p className="text-xs text-text-3">Aucun agenceur — ajoutez-en un.</p>}
                  </div>
                </div>

                {/* COPIES (Cc) & EXPÉDITEUR */}
                <div className="rounded-card bg-surface p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display font-bold uppercase tracking-[0.02em] text-ink text-lg">Copie systématique (Cc) &amp; Expéditeur</h2>
                    <button onClick={() => setCcRows((l) => [...l, { id: uid(), role: 'Nouvelle copie', userId: '', email: '' }])} data-testid="croquis-cc-add" className="inline-flex items-center gap-1 h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-ink hover:bg-surface-2"><Plus className="w-3.5 h-3.5" /> Ajouter une copie</button>
                  </div>
                  <div className="space-y-2 mb-4" data-testid="croquis-cc-list">
                    {ccRows.map((cp, i) => {
                      const bad = cp.email && !emailOk(cp.email);
                      return (
                      <div key={cp.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`croquis-cc-row-${i}`}>
                        <input value={cp.role || ''} onChange={(e) => setCcRows((l) => l.map((x) => x.id === cp.id ? { ...x, role: e.target.value } : x))} placeholder="Libellé (ex : Assistante commerciale)" className={`${inp} md:col-span-4`} data-testid={`croquis-cc-role-${i}`} />
                        <select value={cp.userId || ''} onChange={(e) => { const u = usersList.find((x) => x.id === e.target.value); setCcRows((l) => l.map((x) => x.id === cp.id ? { ...x, userId: e.target.value, email: u?.email || x.email } : x)); }} className={`${inp} md:col-span-4`} data-testid={`croquis-cc-${i}`}>
                          <option value="">— email manuel —</option>
                          {usersList.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
                        </select>
                        <input value={cp.email || ''} onChange={(e) => setCcRows((l) => l.map((x) => x.id === cp.id ? { ...x, email: e.target.value } : x))} placeholder="email" className={`${inp} md:col-span-3`} style={bad ? { borderColor: '#B42318' } : {}} data-testid={`croquis-cc-email-${i}`} />
                        <button onClick={() => setCcRows((l) => l.filter((x) => x.id !== cp.id))} data-testid={`croquis-cc-del-${i}`} aria-label="Supprimer la copie" className="md:col-span-1 h-11 px-3 rounded-ctrl justify-self-start bg-surface border border-border text-danger hover:bg-surface-2"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      );
                    })}
                    {ccRows.length === 0 && <p className="text-xs text-text-3">Aucune copie — ajoutez-en une.</p>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className={lbl}>Expéditeur (nom)</label><input value={(reglages.expediteur || {}).nom || ''} onChange={(e) => setReglages((rg) => ({ ...rg, expediteur: { ...(rg.expediteur || {}), nom: e.target.value } }))} className={inp} /></div>
                    <div><label className={lbl}>Expéditeur (email Super Admin)</label><input value={(reglages.expediteur || {}).email || ''} onChange={(e) => setReglages((rg) => ({ ...rg, expediteur: { ...(rg.expediteur || {}), email: e.target.value } }))} className={inp} style={!emailOk((reglages.expediteur || {}).email) ? { borderColor: '#B42318' } : {}} data-testid="croquis-expediteur-email" /></div>
                  </div>
                  <button onClick={saveReglages} data-testid="croquis-reglages-save" className={`${btnPrimary} mt-4`}>Enregistrer les réglages</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Fenêtre de confirmation « Envoi à l'agenceur » §5.3.5 */}
      {showSendModal && isAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowSendModal(false)} data-testid="croquis-send-modal-overlay">
          <div className="w-full max-w-lg max-h-[90vh] overflow-auto rounded-scene bg-surface p-6" onClick={(e) => e.stopPropagation()} data-testid="croquis-envoi-agenceur">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-label mb-1">Projets corners · Confirmation</p>
                <h3 className="font-display font-bold uppercase text-2xl text-ink leading-none">Envoi à l'agenceur</h3>
              </div>
              <button onClick={() => setShowSendModal(false)} aria-label="Fermer" className="w-9 h-9 rounded-ctrl flex items-center justify-center text-text-2 hover:bg-surface-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="rounded-ctrl bg-surface-2 p-4 mb-4 space-y-1">
              <div className="flex gap-2 text-sm"><span className="uppercase text-[11px] font-bold w-10 shrink-0 text-label pt-0.5">De</span><span className="text-text-2">{`${exp.nom || 'Super Admin'} : ${exp.email || 'email à compléter dans Réglages'}`}</span></div>
              <div className="flex gap-2 text-sm"><span className="uppercase text-[11px] font-bold w-10 shrink-0 text-label pt-0.5">À</span><span className="text-text-2">{ag ? `${ag.contact || ag.societe} : ${ag.email || 'email à compléter dans Réglages'}` : 'Agenceur à sélectionner'}</span></div>
              {ccList.map((c, i) => <div key={i} className="flex gap-2 text-sm"><span className="uppercase text-[11px] font-bold w-10 shrink-0 text-label pt-0.5">{i === 0 ? 'Cc' : ''}</span><span className="text-text-2">{c}</span></div>)}
              <div className="flex gap-2 text-sm"><span className="uppercase text-[11px] font-bold w-10 shrink-0 text-label pt-0.5">PJ</span><span className="text-text-2">{pjName}</span></div>
            </div>
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className={lbl + ' mb-0'}>Objet</label>
                {emailEdited && <button onClick={() => { setEmailEdited(false); setEmailSubject(defSubject); setEmailBody(defBody); }} data-testid="croquis-email-reset" className="text-[11px] underline text-text-3">Réinitialiser</button>}
              </div>
              <input value={subjVal} onChange={(e) => { startEmailEdit(); setEmailSubject(e.target.value); }} className={inp} data-testid="croquis-email-subject" />
            </div>
            <div className="mb-4">
              <label className={lbl}>Message</label>
              <textarea value={bodyVal} onChange={(e) => { startEmailEdit(); setEmailBody(e.target.value); }} rows={8} className={`${inp} h-auto py-2`} data-testid="croquis-email-body" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSendModal(false)} className={btnSecLight}>Annuler</button>
              <button onClick={sendCroquis} disabled={busy === 'send'} data-testid="croquis-send" className={btnPrimary}>
                <Send className="w-4 h-4" /> {busy === 'send' ? 'Envoi…' : 'Envoyer croquis'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
